import type { AcademyChapter, AcademyModule } from '@platform/shared-types';
import type { ModuleWithChapters } from '../../services/academyApi';

// Write plumbing for the Curriculum tab, kept free of React so the ordering
// guarantees are unit-tested (moduleWrites.test.ts).
//
// `saveModule` replaces a module's WHOLE record and chapter set, so every
// queued write must build its payload from the result of the write before
// it. A queued thunk runs one microtask after the previous write settles --
// before React re-renders -- so the ref those thunks read must be updated by
// the write itself, not by a render/effect afterwards.

/** Runs a write. Writes are SERIALISED (never concurrent); a failure never blocks the next one. */
export type Enqueue = <T>(fn: () => Promise<T>) => Promise<T>;

export function createSerialQueue(): Enqueue {
  let tail: Promise<unknown> = Promise.resolve();
  return <T>(fn: () => Promise<T>) => {
    const run = tail.then(fn, fn);
    tail = run.catch(() => undefined);
    return run;
  };
}

/** Replaces (or appends) one module, keeping curriculum order. */
export function spliceModule(list: ModuleWithChapters[], updated: ModuleWithChapters): ModuleWithChapters[] {
  const id = updated.module.moduleId;
  const next = list.some((m) => m.module.moduleId === id)
    ? list.map((m) => (m.module.moduleId === id ? updated : m))
    : [...list, updated];
  return [...next].sort((a, b) => a.module.order - b.module.order);
}

export type SaveModuleFn = (
  moduleId: string, module: Partial<AcademyModule>, chapters: AcademyChapter[],
) => Promise<ModuleWithChapters>;

/**
 * Saves, then writes the saved result into `ref.current` BEFORE resolving,
 * so the next queued thunk reads it. Use at every saveModule call site.
 */
export async function saveSynced(
  ref: { current: ModuleWithChapters[] },
  save: SaveModuleFn,
  moduleId: string,
  module: Partial<AcademyModule>,
  chapters: AcademyChapter[],
): Promise<ModuleWithChapters> {
  const saved = await save(moduleId, module, chapters);
  ref.current = spliceModule(ref.current, saved);
  return saved;
}

const current = (ref: { current: ModuleWithChapters[] }, moduleId: string) => {
  const m = ref.current.find((x) => x.module.moduleId === moduleId);
  if (!m) throw new Error('That module is no longer loaded — reload the page.');
  return m;
};

/**
 * Module-field write (title, category, packageIds…). Reads the module AT RUN
 * TIME and passes its chapters straight through -- otherwise the save would
 * delete every chapter, or roll back a field saved just before.
 */
export const patchModule = (
  ref: { current: ModuleWithChapters[] }, save: SaveModuleFn, moduleId: string, patch: Partial<AcademyModule>,
) => {
  const m = current(ref, moduleId);
  return saveSynced(ref, save, moduleId, { ...m.module, ...patch }, m.chapters);
};

/** Chapter-set write (add / edit a chapter). Same run-time read; the module record passes through. */
export const updateChapters = (
  ref: { current: ModuleWithChapters[] }, save: SaveModuleFn, moduleId: string,
  change: (chapters: AcademyChapter[]) => AcademyChapter[],
) => {
  const m = current(ref, moduleId);
  return saveSynced(ref, save, moduleId, m.module, change(m.chapters));
};

/** Who a module is for. Learn (tenant surfaces) needs `customer` or `employee`; certification needs `affiliate`. */
export type ModuleAudience = AcademyModule['audience'][number];

/**
 * Toggles one audience on or off. A module must keep at least one audience --
 * returns null when the toggle would leave none, so the caller shows a hint
 * instead of saving. Order follows `all` so the saved list is stable.
 */
export function toggleAudience(
  current: ModuleAudience[], value: ModuleAudience, all: readonly ModuleAudience[],
): ModuleAudience[] | null {
  const on = new Set(current);
  if (on.has(value)) on.delete(value); else on.add(value);
  if (on.size === 0) return null;
  return all.filter((a) => on.has(a));
}
